import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import { Field, LoadingState, Panel, StatusBadge } from '../components/ui.js';
import { RaceSelect } from '../components/RaceSelect.js';
import { num } from '../lib/format.js';

/**
 * Průvodce novým segmentem: výběry místo volných polí, počty u každé nabídky a živý odhad dosahu.
 * Technická pole zůstávají v „Všechny podmínky“ níže na stránce.
 */

export interface BuilderDefinition {
  series?: string;
  raceId?: string;
  interest?: 'purchased' | 'viewed' | 'any';
  watchingRaceId?: string;
  orderedRaceId?: string;
  notOrderedRaceId?: string;
  orderedSeries?: string;
  notOrderedSeries?: string;
  boughtWithinMonths?: number;
  activityTier?: Array<'active' | 'slowing' | 'sleeping' | 'dead'>;
  webEngagedWithinDays?: number;
  webActiveWithinDays?: number;
  webKeyEvent?: string;
  webKeyEventWithinDays?: number;
  engagedWithinDays?: number;
  dormantForDays?: number;
  clickedMessageCode?: string;
  clickedWithinDays?: number;
  language?: string;
  seller?: string;
  orderedEvent?: string;
  watchingEvent?: string;
  orderedUpcoming?: boolean;
  notOrderedUpcoming?: boolean;
  hadWatchdog?: boolean;
  watchingUpcoming?: boolean;
  seInterestAny?: boolean;
  minOrders?: number;
  seInterestRace?: string;
  anyOf?: BuilderDefinition[];
  marketable?: boolean;
  consent?: string;
  excludeHoldout?: boolean;
  neverBought?: boolean;
}

interface Options {
  races: Array<{
    raceId: string;
    name: string;
    series: string;
    season: number;
    dateStart: string | null;
    phase?: 'past' | 'ongoing' | 'upcoming' | 'undated';
    cancelled?: boolean;
    buyers: number;
    watchdogs: number;
  }>;
  series: Array<{ code: string; races: number }>;
  languages: Array<{ code: string; n: number }>;
  tiers: Array<{ tier: string; n: number }>;
  messages: Array<{ code: string; name: string }>;
  keyEvents: string[];
  events: Array<{
    event: string;
    name: string;
    editions: number;
    buyers: number;
    watchers: number;
  }>;
  seRaces: Array<{ race: string; lists: number }>;
}

interface Preview {
  members: number;
  marketable: number;
  matching: number;
  blockedBy: { noConsent: number; unsubscribed: number; blockedAddress: number; holdout: number };
  byLanguage: Array<{ language: string; n: number }>;
  sample: Array<{ id: string; email: string; language: string | null }>;
  mirror: { members: number; afterExclusions: number } | null;
  popis: string;
}

type Relation = 'any' | 'bought' | 'watching' | 'interested' | 'not_bought';

const RELATION: Record<Relation, string> = {
  any: 'Kdokoli (bez ohledu na závod)',
  bought: 'Koupili tento závod',
  watching: 'Hlídají tento závod (hlídací pes)',
  interested: 'Zajímali se o něj na webu',
  not_bought: 'Zajímali se, ale nekoupili',
};

const TIER: Record<string, string> = {
  active: 'Aktivní',
  slowing: 'Zvolna ztrácí zájem',
  sleeping: 'Spící',
  dead: 'Mrtví',
  unknown: 'Zatím nezjištěno',
};

const WEB_CHOICES: Array<[string, string, Partial<BuilderDefinition>]> = [
  ['', 'Nerozhoduje', {}],
  ['v7', 'Byl na webu za 7 dní', { webActiveWithinDays: 7 }],
  ['v30', 'Byl na webu za 30 dní', { webActiveWithinDays: 30 }],
  ['a14', 'Aktivní na webu za 14 dní', { webEngagedWithinDays: 14 }],
  [
    'c14',
    'Začal objednávku (do 14 dní)',
    { webKeyEvent: 'begin_checkout', webKeyEventWithinDays: 14 },
  ],
];

const REACT_CHOICES: Array<[string, string, Partial<BuilderDefinition>]> = [
  ['', 'Nerozhoduje', {}],
  ['e30', 'Reagoval za posledních 30 dní', { engagedWithinDays: 30 }],
  ['e90', 'Reagoval za posledních 90 dní', { engagedWithinDays: 90 }],
  ['d90', 'Nereagoval 90 dní', { dormantForDays: 90 }],
  ['d180', 'Nereagoval 180 dní', { dormantForDays: 180 }],
];

const slug = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 36);

export function SegmentBuilder({ onSaved }: { onSaved: (text: string) => void }): JSX.Element {
  const client = useQueryClient();
  const options = useQuery({
    queryKey: ['segment-builder-options'],
    queryFn: () => api.get<Options>('/api/segments/builder-options'),
    staleTime: 60_000,
  });
  const [raceId, setRaceId] = useState('');
  const [series, setSeries] = useState('');
  const [relation, setRelation] = useState<Relation>('any');
  const [tiers, setTiers] = useState<string[]>([]);
  const [language, setLanguage] = useState('');
  const [seller, setSeller] = useState('');
  const [web, setWeb] = useState('');
  const [react, setReact] = useState('');
  const [onlyMarketable, setOnlyMarketable] = useState(true);
  const [name, setName] = useState('');
  const [push, setPush] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clickMsg, setClickMsg] = useState('');
  const [upcoming, setUpcoming] = useState('');
  const [dog, setDog] = useState('');
  const [bought, setBought] = useState('');
  const [seAny, setSeAny] = useState(false);
  const [event, setEvent] = useState('');
  const [seRace, setSeRace] = useState('');
  const [useBuyers, setUseBuyers] = useState(true);
  const [useWatchers, setUseWatchers] = useState(true);
  const [useSe, setUseSe] = useState(true);
  const [skipBuyers, setSkipBuyers] = useState(true);
  // Přípona kódu: dva segmenty se stejným názvem se nesmí přepsat.
  const [suffix] = useState(() => Date.now().toString(36).slice(-4));

  const definition: BuilderDefinition = { interest: 'any' };
  /* Zájem o událost (všechny ročníky): stačí jedno z vybraného; závod se pak jen odečítá od kupujících. */
  const eventAlts: BuilderDefinition[] = [];
  if (event && useBuyers) eventAlts.push({ orderedEvent: event });
  if (event && useWatchers) eventAlts.push({ watchingEvent: event });
  if (seRace && useSe) eventAlts.push({ seInterestRace: seRace });
  const eventMode = eventAlts.length > 0;
  if (eventMode) {
    definition.anyOf = eventAlts;
    if (raceId && skipBuyers) definition.notOrderedRaceId = raceId;
  }
  if (series && !eventMode) definition.series = series;
  if (raceId && !eventMode) {
    if (relation === 'bought') Object.assign(definition, { orderedRaceId: raceId });
    else if (relation === 'watching') definition.watchingRaceId = raceId;
    else if (relation === 'interested')
      Object.assign(definition, { raceId, interest: 'viewed', webActiveWithinDays: 30 });
    else if (relation === 'not_bought')
      Object.assign(definition, { raceId, interest: 'viewed', notOrderedRaceId: raceId });
    else Object.assign(definition, { raceId });
  }
  if (tiers.length > 0) definition.activityTier = tiers as BuilderDefinition['activityTier'];
  if (clickMsg) Object.assign(definition, { clickedMessageCode: clickMsg, clickedWithinDays: 30 });
  /* Obecné podmínky přes všechny závody (historické i budoucí). */
  if (upcoming === 'has') definition.orderedUpcoming = true;
  if (upcoming === 'not') definition.notOrderedUpcoming = true;
  if (dog === 'upcoming') definition.watchingUpcoming = true;
  if (dog === 'ever') definition.hadWatchdog = true;
  if (bought === 'ever') definition.minOrders = 1;
  if (bought === 'never') definition.neverBought = true;
  if (seAny) definition.seInterestAny = true;
  if (language) definition.language = language;
  if (seller) definition.seller = seller;
  Object.assign(definition, WEB_CHOICES.find((c) => c[0] === web)?.[2] ?? {});
  Object.assign(definition, REACT_CHOICES.find((c) => c[0] === react)?.[2] ?? {});
  if (onlyMarketable) {
    definition.marketable = true;
    definition.consent = 'granted';
    definition.excludeHoldout = true;
  }

  const [preview, setPreview] = useState<Preview | null>(null);
  const [pending, setPending] = useState(false);
  const key = JSON.stringify(definition);
  useEffect(() => {
    let cancelled = false;
    setPending(true);
    const t = setTimeout(() => {
      api
        .post<Preview>('/api/segments/preview', { definition, sampleSize: 5 })
        .then((p) => {
          if (!cancelled) {
            setPreview(p);
            setError(null);
          }
        })
        .catch((e: Error) => {
          if (!cancelled) setError(e.message);
        })
        .finally(() => {
          if (!cancelled) setPending(false);
        });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [key]);

  const race = options.data?.races.find((r) => r.raceId === raceId);
  const suggestedName = [
    race ? race.name : series ? series.toUpperCase() : 'Všichni',
    raceId && relation !== 'any' ? RELATION[relation].toLowerCase() : '',
    tiers.length > 0 ? tiers.map((t) => TIER[t]?.toLowerCase()).join('/') : '',
    language ? language : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const finalName = name.trim() || suggestedName;
  const code = `${slug(finalName) || 'segment'}_${suffix}`;

  const save = useMutation({
    mutationFn: () =>
      api.put<{ members: number }>(`/api/segments/${encodeURIComponent(code)}`, {
        code,
        name: finalName,
        definition,
        enabled: true,
        pushToProvider: push,
      }),
    onSuccess: (d) => {
      setError(null);
      onSaved(`Segment „${finalName}“ uložen, má ${num(d.members)} kontaktů.`);
      void client.invalidateQueries({ queryKey: ['segments'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (options.isLoading) return <LoadingState what="nabídky segmentů" />;
  const o = options.data;
  const messages = [...new Map((o?.messages ?? []).map((m) => [m.code, m])).values()];

  return (
    <Panel
      title="Nový segment"
      subtitle="Vyberte, koho chcete oslovit. Počet kontaktů vpravo se přepočítává sám."
    >
      <div className="seg-builder" data-testid="segment-builder">
        <div className="stack">
          <div className="stack seg-event" data-testid="segment-event">
            <h3>Zájemci o událost (všechny ročníky)</h3>
            <p className="note">
              Pro lidi, kteří se o událost zajímali kdykoli v minulosti: kupovali, hlídali, nebo je
              k ní dostávali ve SmartEmailingu. Stačí, když splní jednu z vybraných možností.
            </p>
            <div className="row">
              <Field label="Událost v hubu">
                <select
                  value={event}
                  onChange={(e) => setEvent(e.target.value)}
                  aria-label="Událost"
                >
                  <option value="">— nevybráno —</option>
                  {(o?.events ?? []).map((ev) => (
                    <option key={ev.event} value={ev.event}>
                      {ev.name} · {num(ev.editions)} ročníků · koupilo {num(ev.buyers)} · hlídalo{' '}
                      {num(ev.watchers)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Seznamy ve SmartEmailingu">
                <select
                  value={seRace}
                  onChange={(e) => setSeRace(e.target.value)}
                  aria-label="Seznamy SE"
                >
                  <option value="">— nevybráno —</option>
                  {(o?.seRaces ?? []).map((r) => (
                    <option key={r.race} value={r.race}>
                      {r.race} · {num(r.lists)} seznamů
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            {event || seRace ? (
              <>
                <label className="row">
                  <input
                    type="checkbox"
                    checked={useBuyers}
                    disabled={!event}
                    onChange={(e) => setUseBuyers(e.target.checked)}
                  />
                  <span>
                    <b>Koupili některý ročník</b>{' '}
                    <small className="muted">
                      Platící zákazníci, nejspolehlivější publikum: událost znají a už jednou
                      koupili.
                    </small>
                  </span>
                </label>
                <label className="row">
                  <input
                    type="checkbox"
                    checked={useWatchers}
                    disabled={!event}
                    onChange={(e) => setUseWatchers(e.target.checked)}
                  />
                  <span>
                    <b>Hlídali některý ročník (hlídací pes)</b>{' '}
                    <small className="muted">
                      Chtěli vstupenku, ale nebyla nebo nekoupili; zájem projevili sami. Počítají se
                      i hlídání z minulých ročníků.
                    </small>
                  </span>
                </label>
                <label className="row">
                  <input
                    type="checkbox"
                    checked={useSe}
                    disabled={!seRace}
                    onChange={(e) => setUseSe(e.target.checked)}
                  />
                  <span>
                    <b>Zájem podle seznamů ve SmartEmailingu</b>{' '}
                    <small className="muted">
                      Lidé, kterým se událost mailovala, protože byli v seznamu zájemců, hlídačů
                      nebo kupujících.
                    </small>
                  </span>
                </label>
                <label className="row">
                  <input
                    type="checkbox"
                    checked={skipBuyers}
                    disabled={!raceId}
                    onChange={(e) => setSkipBuyers(e.target.checked)}
                  />
                  <span>
                    <b>Nepsat těm, kdo už koupili závod níže</b>{' '}
                    <small className="muted">
                      Vyberte nadcházející závod v bodě 1; kdo už má vstupenky, reklamu nepotřebuje.
                    </small>
                  </span>
                </label>
                {eventMode ? (
                  <div className="note" data-testid="segment-why">
                    <b>Jak je segment postavený a proč:</b>
                    <ol>
                      {eventAlts.map((a) => (
                        <li key={JSON.stringify(a)}>
                          {a.orderedEvent
                            ? 'Kupující: kdo zaplatil objednávku na kterýkoli ročník události.'
                            : a.watchingEvent
                              ? 'Hlídači: kdo měl potvrzeného hlídacího psa na kterýkoli ročník.'
                              : 'SmartEmailing: kdo je potvrzeným členem seznamů zájemců o událost.'}
                        </li>
                      ))}
                      <li>
                        Stačí splnit jedno z výše uvedeného (sloučení bez duplicit). Dál se pro
                        všechny automaticky platí: souhlas s marketingem, žádné odhlášení ani
                        blokace, žádná kontrolní skupina.
                      </li>
                      {raceId && skipBuyers ? (
                        <li>Z toho jsou vyřazeni ti, kdo už koupili vybraný závod.</li>
                      ) : null}
                    </ol>
                    <p>
                      Segment je živý: členství se přepočítává každou noc a znovu před každým
                      náhledem rozesílky, takže odhlášení a noví zájemci se promítnou sami.
                    </p>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>

          <Field
            label="1. Závod"
            hint="Nechte prázdné, chcete-li oslovit lidi bez ohledu na závod."
          >
            <RaceSelect
              races={o?.races ?? []}
              value={raceId}
              emptyLabel="— žádný konkrétní závod —"
              onChange={setRaceId}
            />
          </Field>
          {raceId ? (
            <Field label="2. Vztah k závodu">
              <select
                value={relation}
                onChange={(e) => setRelation(e.target.value as Relation)}
                aria-label="Vztah k závodu"
              >
                {Object.entries(RELATION).map(([k, text]) => (
                  <option key={k} value={k}>
                    {text}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Field label="2. Série" hint="Kupovali nebo prohlíželi tuto sérii.">
              <select value={series} onChange={(e) => setSeries(e.target.value)} aria-label="Série">
                <option value="">— všechny série —</option>
                {(o?.series ?? []).map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code.toUpperCase()} · {num(s.races)} závodů
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field
            label="3. Jak jsou aktivní"
            hint="Podle toho, jak reagují na e-maily a web. Nic = všichni."
          >
            <div className="row" role="group" aria-label="Aktivita">
              {(o?.tiers ?? [])
                .filter((t) => t.tier !== 'unknown')
                .map((t) => (
                  <label key={t.tier} className="row">
                    <input
                      type="checkbox"
                      checked={tiers.includes(t.tier)}
                      onChange={(e) =>
                        setTiers(
                          e.target.checked ? [...tiers, t.tier] : tiers.filter((x) => x !== t.tier),
                        )
                      }
                    />
                    <span>
                      {TIER[t.tier] ?? t.tier} <small className="muted">({num(t.n)})</small>
                    </span>
                  </label>
                ))}
            </div>
          </Field>

          <div className="row">
            <Field label="4. Chování na webu">
              <select value={web} onChange={(e) => setWeb(e.target.value)}>
                {WEB_CHOICES.map(([k, text]) => (
                  <option key={k} value={k}>
                    {text}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="5. Reakce na e-maily">
              <select value={react} onChange={(e) => setReact(e.target.value)}>
                {REACT_CHOICES.map(([k, text]) => (
                  <option key={k} value={k}>
                    {text}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="row">
            <Field label="6. Jazyk">
              <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="">všechny jazyky</option>
                {(o?.languages ?? []).map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.code} · {num(l.n)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="7. Značka">
              <BrandSelect value={seller} onChange={setSeller} allLabel="všechny značky" />
            </Field>
          </div>

          <div className="stack" data-testid="segment-general">
            <h3>Obecně přes všechny závody (historické i budoucí)</h3>
            <div className="row">
              <Field label="Nákup">
                <select
                  value={bought}
                  onChange={(e) => setBought(e.target.value)}
                  aria-label="Nákup"
                >
                  <option value="">nerozhoduje</option>
                  <option value="ever">někdy koupil</option>
                  <option value="never">nikdy nekoupil</option>
                </select>
              </Field>
              <Field label="Budoucí závod">
                <select
                  value={upcoming}
                  onChange={(e) => setUpcoming(e.target.value)}
                  aria-label="Budoucí závod"
                >
                  <option value="">nerozhoduje</option>
                  <option value="has">má nákup na budoucí závod</option>
                  <option value="not">nemá nákup na žádný budoucí závod</option>
                </select>
              </Field>
              <Field label="Hlídací pes">
                <select
                  value={dog}
                  onChange={(e) => setDog(e.target.value)}
                  aria-label="Hlídací pes"
                >
                  <option value="">nerozhoduje</option>
                  <option value="upcoming">hlídá budoucí závod</option>
                  <option value="ever">někdy měl hlídacího psa</option>
                </select>
              </Field>
            </div>
            <label className="row">
              <input type="checkbox" checked={seAny} onChange={(e) => setSeAny(e.target.checked)} />
              <span>
                <b>Zájem podle seznamů ve SmartEmailingu</b>{' '}
                <small className="muted">
                  (člen seznamu zájmu, hlídání nebo kupujících; data ze SmartEmailingu)
                </small>
              </span>
            </label>
            <p className="note">
              Budoucí závod = ještě neskončil (nebo nemá datum), počítá se z kalendáře vždy
              aktuálně. Podmínky platí zároveň (a), mezi možnostmi Zájemci o událost výše stačí
              jedna.
            </p>
          </div>

          <Field label="8. Klikl ve zprávě" hint="Za posledních 30 dní.">
            <select value={clickMsg} onChange={(e) => setClickMsg(e.target.value)}>
              <option value="">nerozhoduje</option>
              {messages.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.code} · {m.name}
                </option>
              ))}
            </select>
          </Field>

          <label className="row">
            <input
              type="checkbox"
              checked={onlyMarketable}
              onChange={(e) => setOnlyMarketable(e.target.checked)}
            />
            <span>
              <b>Jen ti, kterým smíme poslat marketing</b>{' '}
              <small className="muted">
                (mají souhlas, nejsou odhlášení ani zablokovaní, nejsou v kontrolní skupině)
              </small>
            </span>
          </label>
        </div>

        <aside className="stack seg-reach" aria-live="polite" data-testid="segment-reach">
          <h3>Dosah</h3>
          {preview ? (
            <>
              <p className="seg-big">
                <b data-testid="segment-count">{num(preview.matching)}</b> kontaktů odpovídá
                {pending ? <small className="muted"> (počítám…)</small> : null}
              </p>
              <p>
                Z nich smí dostat marketing: <b>{num(preview.marketable)}</b>
              </p>
              {preview.matching > preview.marketable ? (
                <p className="note" data-testid="segment-blocked">
                  Nesmí: bez souhlasu {num(preview.blockedBy.noConsent)} · odhlášení ve
                  SmartEmailingu {num(preview.blockedBy.unsubscribed)} · blokovaná adresa nebo
                  odskok {num(preview.blockedBy.blockedAddress)} · kontrolní skupina{' '}
                  {num(preview.blockedBy.holdout)} (kontakty se počítají v každé skupině zvlášť,
                  součet může být vyšší).
                </p>
              ) : null}
              {preview.mirror ? (
                <p>
                  Ve SmartEmailingu: <b>{num(preview.mirror.members)}</b> adres (po výlukách{' '}
                  {num(preview.mirror.afterExclusions)})
                </p>
              ) : null}
              {preview.byLanguage.length > 0 ? (
                <p className="note">
                  Jazyky: {preview.byLanguage.map((l) => `${l.language} ${num(l.n)}`).join(' · ')}
                </p>
              ) : null}
              <p className="note">Podmínky: {preview.popis}.</p>
              {preview.sample.length > 0 ? (
                <p className="note">Ukázka: {preview.sample.map((s) => s.email).join(', ')}</p>
              ) : null}
            </>
          ) : (
            <p className="muted">Počítám…</p>
          )}
          {error ? (
            <StatusBadge tone="bad" icon="alert">
              {error}
            </StatusBadge>
          ) : null}
          <Field label="Název segmentu" hint="Když nic nenapíšete, použije se navržený.">
            <input
              value={name}
              placeholder={suggestedName}
              onChange={(e) => setName(e.target.value)}
              aria-label="Název segmentu"
            />
          </Field>
          <label className="row">
            <input type="checkbox" checked={push} onChange={(e) => setPush(e.target.checked)} />
            <span className="note">Posílat členství do SmartEmailingu</span>
          </label>
          <button
            type="button"
            className="btn pri"
            disabled={save.isPending || !preview || preview.members === 0}
            onClick={() => save.mutate()}
            data-testid="segment-builder-save"
          >
            Uložit segment
          </button>
        </aside>
      </div>
    </Panel>
  );
}
